import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData } from '@tanstack/react-query';
import { Table, Space, Input, Select, Segmented, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useWisecureQuery } from '@wisecure/api-client/hooks';
import type {
  AdminSecurityRequestResponse,
  SecurityRequestCountsResponse,
  SecurityRequestStatus,
  SecurityRequestType,
} from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import type { PaginatedResponse } from '@/types/dashboard';
import { neutral } from '@/theme';
import {
  REQUEST_TYPE_LABELS,
  clientDisplayName,
  formatDateTime,
  formatEuro,
  toOptions,
} from '@/components/security/labels';
import { CategoryTags, RequestTypeTag } from '@/components/security/RequestTags';
import {
  SecurityRequestStatusSwitch,
  SecurityRequestStatusTag,
} from '@/components/security/SecurityRequestStatus';

type StatusFilter = SecurityRequestStatus | 'ALL';

const subText = { fontSize: 12, color: neutral[500] } as const;

export function SecurityRequestsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [status, setStatus] = useState<StatusFilter>('PENDING');
  const [type, setType] = useState<SecurityRequestType | undefined>(undefined);
  const [search, setSearch] = useState('');

  const filters = {
    page,
    limit,
    ...(status !== 'ALL' ? { status } : {}),
    ...(type ? { type } : {}),
    ...(search ? { search } : {}),
  };

  const { data, isLoading, isFetching } = useWisecureQuery<PaginatedResponse<AdminSecurityRequestResponse>>(
    queryKeys.securityRequests(filters),
    (client) =>
      client.dashboardSecurity.securityDashboardControllerFindAll(filters) as Promise<
        PaginatedResponse<AdminSecurityRequestResponse>
      >,
    { placeholderData: keepPreviousData },
  );

  const { data: counts } = useWisecureQuery<SecurityRequestCountsResponse>(
    queryKeys.securityRequestCounts,
    (client) => client.dashboardSecurity.securityDashboardControllerCounts(),
  );

  const handleTableChange = useCallback((pagination: { current?: number; pageSize?: number }) => {
    if (pagination.current) setPage(pagination.current);
    if (pagination.pageSize) setLimit(pagination.pageSize);
  }, []);

  const countLabel = (label: string, count?: number) => (count == null ? label : `${label} (${count})`);

  const columns: ColumnsType<AdminSecurityRequestResponse> = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (date: string) => formatDateTime(date),
    },
    {
      title: 'Client',
      key: 'client',
      width: 220,
      render: (_, { client }) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{clientDisplayName(client)}</div>
          <div style={subText}>{client.email}</div>
          {client.phone && <div style={subText}>{client.phone}</div>}
        </div>
      ),
    },
    {
      title: 'Lieu',
      key: 'location',
      width: 240,
      render: (_, { location }) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{location.label}</div>
          <div style={subText}>{location.address}</div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (value: SecurityRequestType) => <RequestTypeTag type={value} />,
    },
    {
      title: 'Équipements',
      dataIndex: 'categories',
      key: 'categories',
      width: 180,
      render: (_, record) => <CategoryTags categories={record.categories} />,
    },
    {
      title: 'Formule',
      dataIndex: 'summary',
      key: 'summary',
      width: 200,
      render: (summary?: string | null) => summary || '—',
    },
    {
      title: 'Prix estimé',
      dataIndex: 'estimatedPrice',
      key: 'estimatedPrice',
      width: 120,
      align: 'right',
      render: (price?: number | null) => formatEuro(price),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value: SecurityRequestStatus) => <SecurityRequestStatusTag status={value} />,
    },
    {
      title: 'Traitement',
      key: 'toggle',
      width: 130,
      render: (_, record) => <SecurityRequestStatusSwitch id={record.id} status={record.status} size="small" />,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Demandes sécurité"
        subtitle={`${data?.total ?? 0} demande(s)`}
        extra={isFetching ? <Spin size="small" /> : undefined}
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Segmented<StatusFilter>
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
          options={[
            { value: 'PENDING', label: countLabel('Non traitées', counts?.pending) },
            { value: 'HANDLED', label: countLabel('Traitées', counts?.handled) },
            { value: 'ALL', label: 'Toutes' },
          ]}
        />
        <Select<SecurityRequestType>
          value={type}
          onChange={(value) => {
            setType(value);
            setPage(1);
          }}
          options={toOptions(REQUEST_TYPE_LABELS)}
          allowClear
          placeholder="Tous les types"
          style={{ width: 180 }}
        />
        <Input.Search
          placeholder="Rechercher par client, e-mail, téléphone, adresse..."
          allowClear
          onSearch={(value) => {
            setSearch(value.trim());
            setPage(1);
          }}
          onChange={(e) => {
            if (!e.target.value) {
              setSearch('');
              setPage(1);
            }
          }}
          style={{ width: 340 }}
        />
      </Space>

      <Table<AdminSecurityRequestResponse>
        columns={columns}
        dataSource={data?.data ?? []}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: 1300 }}
        pagination={{
          current: page,
          pageSize: limit,
          total: data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
        }}
        onChange={handleTableChange as () => void}
        onRow={(record) => ({
          onClick: () => navigate(`/security-requests/${record.id}`),
          style: { cursor: 'pointer' },
        })}
        locale={{ emptyText: 'Aucune demande' }}
        size="middle"
      />
    </div>
  );
}
